import React from 'react';
import { observer } from 'mobx-react-lite';
import { useFullFormPageStore } from 'store/pages/enrollment/add-employees/full-form/pageStore';
import { DynamicFormModal } from '@insurancemenu/imenu-components-lib';
import SubscriberCard from 'components/common/election/cards/SubscriberCard';
import { toJS, action } from 'mobx';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { DependentsChoice, UpdatedPlanInfo } from 'types/electionService';
import { useFullEmployeeFormUrlParams } from 'global/hooks/useEmployeeFullFormUrlParams';
import { TabId } from 'store/modules/full-employee-form/types';
import { PRODUCT_LINE_RATE_MODAL_TEST_ID, ELECTIONS_TAB_TEST_ID } from '../../constants';

const ElectionsTab: React.FC = observer(() => {
  const { electionsModule } = useFullFormPageStore();

  const { subscriberId } = useFullEmployeeFormUrlParams();

  if (electionsModule.subscribers.length === 0) {
    throw new Error('Cannot render ElectionsTab without a subscriber delegate');
  }

  const subscriberAdapter = electionsModule.subscribers[0];
  const subscriberDelegate = subscriberAdapter?.subscriberDelegate;
  if (!subscriberDelegate) {
    throw new Error('Cannot render ElectionsTab without a subscriber delegate');
  }

  const pLineRateModalDelegate = subscriberDelegate.productLineRateModalDelegate;

  /**
   * Even with this re-references, mobx will throw one gazillion warnings because the subscriberAdapter is the observable.
   * The SubscriberCard must be moved from the lib to here and has its own observer wrapper, then we'll be able to use the
   * observables directly and avoid the warnings.
   */
  const subscriber = subscriberDelegate.subscriber;

  const productLineRates = subscriberAdapter?.pLineRates.map((rate) => toJS(rate)).slice();
  const products = electionsModule.products.map((product) => toJS(product)).slice();
  const subscriberClasses = electionsModule.classesAvailableForSelection.map((cls) => toJS(cls)).slice();
  const requiredSubInfo = toJS(electionsModule.electionPresenter) ?? undefined;

  const updateProduct = action((selectedPlanInfo: UpdatedPlanInfo): void => {
    void subscriberAdapter?.syncSelectedPlan(selectedPlanInfo);
  });

  const updateDependentsChoice = action((dependentsChoice: DependentsChoice): void => {
    void subscriberAdapter?.syncDependents(dependentsChoice);
  });

  const updateClass = action((className: string): void => {
    void subscriberAdapter?.syncClassAndResetProductSelection(className);
  });

  const onPLineRateChange = action((formId: string, productId: string): void => {
    void pLineRateModalDelegate.openAndFetchForm(subscriber, formId, productId);
  });

  const onShowDetails = action((productLineId: ProductLineId): void => {
    subscriberAdapter?.redirectToPortfolioDetails(
      productLineId,
      `subscriberId=${subscriberId}&activeTabId=${TabId.ELECTIONS}`,
    );
  });

  return (
    <div data-test-id={ELECTIONS_TAB_TEST_ID}>
      <SubscriberCard
        subscriber={subscriberDelegate.subscriberPresenter}
        productLineRates={productLineRates}
        products={products}
        subscriberClassesMap={{ employeeClasses: subscriberClasses }}
        requiredSubInfo={requiredSubInfo}
        callbacksMap={{
          updateProduct,
          updateDependentsChoice,
          updateClass,
          onPLineRateChange,
          onShowDetails,
        }}
      />
      <DynamicFormModal
        show={pLineRateModalDelegate.isVisible}
        onClose={pLineRateModalDelegate.close}
        formDelegate={pLineRateModalDelegate.dynamicFormDelegate}
        isEditable
        modalMaxWidth="90rem"
        formBodyMinHeight="20rem"
        data-test-id={PRODUCT_LINE_RATE_MODAL_TEST_ID}
      />
    </div>
  );
});

export default ElectionsTab;
